import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useEffect, useState } from 'react';
import ReactFileReader from 'react-file-reader';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import config from '../../../../app/config';
import { color } from '../../../../app/theme';
import { ModalType } from '../../../../app/types';
import { TWD } from '../../../../app/utils';
import IconDelete from '../../../../assets/images/Icons/IconDelete';
import icHelpCircle from '../../../../assets/images/svg/ic-help-circle.svg';
import {
  addDataset,
  deleteDraftDatasets,
  embeddings,
  getDraftDatase,
} from '../../../../features/aiBotSlice';
import errorHandle from '../../../../features/errorHandle';
import { closeModal, openModal } from '../../../../features/modalSlice';
import Breadcrumbs from '../../../shared/Breadcrumbs';
import Button from '../../../shared/Button';
import Checkbox from '../../../shared/Checkbox';
import Tooltip from '../../../shared/Tooltip';
import ImportDataRow from '../ImportDataRow';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 24,
    },
    title: {
      fontSize: '20px',
      lineHeight: '26px',
      color: color.$3,
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      '& > div': {
        fontSize: '13px',
        lineHeight: '13px',
        color: color.$13,
        background: color.$8,
        borderRadius: 12,
        padding: '5px 12px',
        marginLeft: 8,
      },
    },
    subtitle: {
      display: 'flex',
      alignItems: 'center',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      marginBottom: 12,
      '& > div': {
        fontWeight: '600',
      },
      '& > a': {
        color: color.$2,
        textDecorationLine: 'underline',
        marginLeft: 4,
      },
    },
    List: {
      display: 'grid',
      gridTemplateColumns: 'auto 88px 40px',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '13px',
      color: color.$13,
      '& > div': {
        paddingRight: 8,
        display: 'flex',
        alignItems: 'center',
      },
    },
    'List-Head': {
      marginBottom: 12,
      padding: '8px 16px',
    },
    Table: {
      padding: '12px 0',
      maxHeight: '639px',
      overflowY: 'auto',
      borderBottom: `1px solid ${color.$9}`,
    },
    tableBlock: {
      maxWidth: 980,
      borderRadius: 24,
      border: `1px solid ${color.$9}`,
    },
    total: {
      padding: '12px 16px',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      width: '134px',
      textAlign: 'left',
      margin: '0 0 0 auto',
    },
    btn: {
      marginBottom: 12,
    },
    delete: {
      marginRight: 4,
    },
    selectAll: {
      marginBottom: 6,
      maxWidth: 980,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      '& > div': {
        display: 'flex',
        alignItems: 'center',
      },
    },
    deleteAll: {
      cursor: 'pointer',
      color: color.$1,
    },
    totalWordCount: {
      padding: '16px 36px',
      textAlign: 'end',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$13,
      '& > div': {
        marginTop: 8,
      },
      '& span': {
        border: `1px solid ${color.$10}`,
        padding: '2px 6px',
        borderRadius: 8,
      },
      '& a': {
        color: color.$2,
        textDecorationLine: 'underline',
      },
    },
    red: {
      color: color.$17,
    },
    totalCount: {
      fontSize: '13px',
      fontWeight: '600',
      lineHeight: '18.2px',
      color: color.$13,
      marginBottom: 6,
    },
    breadcrumbsList: { marginBottom: 36 },
  },
  {
    name: 'FileImport',
  },
);

const FileImport = ({ pathKey }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { orgId, id } = useParams();
  const { displayName, nextResource } = useSelector(
    store => store.organization,
  );

  const { draftDatasets, datasetUsed } = useSelector(store => store.aiBot);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedList, setSelectedList] = useState([]);

  const quota = nextResource?.resources?.ai_knowledge?.quota || 0;

  const total = datasetUsed + selectedList.length;
  const isError = total > quota;

  const breadcrumbsList = [
    {
      text: t(`${pathKey}:knowledge`),
      url: `/${pathKey}/${orgId}/${id}/knowledge`,
    },
    {
      text: t(`${pathKey}:addFile`),
    },
  ];

  const onImport = async (value, type) => {
    await dispatch(addDataset(orgId, id, value, type));
  };

  const handleImport = async files => {
    setIsLoading(true);
    const file = files?.[0];

    if (file.size > 10000000) {
      dispatch(errorHandle(t(`${pathKey}:error/file_size_exceeded`)));
      setIsLoading(false);
      return;
    }

    await onImport(file, 'file');

    setIsLoading(false);
  };

  const onSelectRow = index => {
    if (selectedList?.find(s => s === index)) {
      setSelectedList(selectedList.filter(s => s !== index));
    } else {
      setSelectedList([...selectedList, index]);
    }
  };

  const onDelete = v => {
    dispatch(
      openModal({
        modalType: ModalType.ALERT_MODAL,
        modalProps: {
          title: t(`${pathKey}:deleteDataset-modal-title`),
          content: t(`${pathKey}:deleteDataset-modal-content`),
          onCancel: () => {
            dispatch(closeModal());
          },
          onClick: async () => {
            await dispatch(deleteDraftDatasets(orgId, id, v));
            setSelectedList([]);
            dispatch(closeModal());
          },
          cancelText: t(`modal.cancel`),
          submitText: t(`modal.sure`),
        },
      }),
    );
  };

  const onEmbeddings = () => {
    dispatch(
      openModal({
        modalType: ModalType.ALERT_MODAL,
        modalProps: {
          title: t(`${pathKey}:embeddings-modal-title`),
          content: t(`${pathKey}:embeddings-modal-content`),
          onCancel: () => {
            dispatch(closeModal());
          },
          onClick: async () => {
            await dispatch(embeddings(orgId, id, 'file'));
            history.push(`/${pathKey}/${orgId}/${id}/knowledge?tab=file`);
            dispatch(closeModal());
          },
          cancelText: t(`modal.cancel`),
          submitText: t(`modal.sure`),
        },
      }),
    );
  };

  const init = async () => {
    setIsLoading(true);
    await dispatch(getDraftDatase(orgId, id, 'file'));
    setIsLoading(false);
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <div className={classes.root}>
      <div className={classes.breadcrumbsList}>
        <Breadcrumbs list={breadcrumbsList} />
      </div>
      <div className={classes.title}>
        {t(`${pathKey}:addFile`)}
        {!!draftDatasets.length && (
          <div>{t(`${pathKey}:statusTypes.init`)}</div>
        )}
      </div>
      <div className={classes.subtitle}>
        <div>{t(`${pathKey}:basicSettingContainer.fileImportSubtitle1`)}</div>
        {t(`${pathKey}:basicSettingContainer.fileImportSubtitle2`)}
        <a
          href={t(`${pathKey}:basicSettingContainer.fileImportSample`)}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t(`${pathKey}:sampleDownload`)}
        </a>
        <Tooltip
          light
          placement="top"
          title={t(`${pathKey}:basicSettingContainer.FileImportToottip`)}
        >
          <img
            style={{ marginLeft: 4 }}
            src={icHelpCircle}
            alt="plan is expired"
          />
        </Tooltip>
      </div>
      <ReactFileReader
        className="react-file-reader"
        fileTypes={['.pdf', '.csv', '.xlsx']}
        handleFiles={handleImport}
      >
        <Button
          className={classes.btn}
          isLoading={isLoading}
          topic="primaryBlue"
        >
          {t(`${pathKey}:basicSettingContainer.fileImport`)}
        </Button>
      </ReactFileReader>
      {!!draftDatasets.length && (
        <>
          <div className={classes.totalCount}>
            {t(`${pathKey}:fileTotalCount`, {
              total: TWD(draftDatasets.length, false).format(),
            })}
          </div>
          {!!selectedList.length && (
            <div className={classes.selectAll}>
              <div>
                <Checkbox
                  checked={selectedList.length === draftDatasets?.length}
                  onClick={() => {
                    if (selectedList.length === draftDatasets?.length) {
                      setSelectedList([]);
                    } else {
                      setSelectedList(draftDatasets.map(s => s?.id));
                    }
                  }}
                />
                {t(`${pathKey}:selected`)} ({selectedList.length})
              </div>
              <div
                className={classes.deleteAll}
                onClick={() => {
                  onDelete(selectedList);
                }}
              >
                <IconDelete
                  className={classes.delete}
                  id="icon-delete-all"
                  width={20}
                  height={20}
                  color={color.$1}
                  hoverColor={color.$1}
                />
                {t(`${pathKey}:multDelete`)}
              </div>
            </div>
          )}
          <div className={classes.tableBlock}>
            <div className={classes.Table}>
              <div className={classnames(classes.List, classes['List-Head'])}>
                <div>{t(`${pathKey}:fileName`)}</div>
                <div>{t(`${pathKey}:basicSettingContainer.words`)}</div>
                <div>{t(`${pathKey}:onDelete`)}</div>
              </div>
              {draftDatasets.map((d, i) => (
                <ImportDataRow
                  key={`csv-${i}`}
                  index={i}
                  data={d}
                  onSelectRow={onSelectRow}
                  onDelete={() => {
                    onDelete([d?.id]);
                  }}
                  isSelected={!!selectedList.find(s => s === d?.id)}
                />
              ))}
            </div>
            <div
              className={classnames(classes.totalWordCount, {
                [classes.red]: isError,
              })}
            >
              {t(`${pathKey}:basicSettingContainer.totalWordCount`)}
              <span>{`${TWD(total, false).format()}/${TWD(
                quota,
                false,
              ).format()}`}</span>
              <div>
                {t(`${pathKey}:basicSettingContainer.totalWordCount1`)}
                <a
                  href="/"
                  rel="noopener noreferrer"
                  target="_blank"
                  onClick={() => {
                    window.open(
                      `mailto:${config.ADDON_UNAVAIABLE_MAIL}?subject=${t(
                        `${pathKey}:embeddingsMaxWordsEmailSubject`,
                      )}&body=${encodeURIComponent(
                        t(`${pathKey}:embeddingsMaxWordsEmailBody`, {
                          orgId,
                          displayName,
                        }),
                      )}`,
                    );
                  }}
                >
                  {t(`${pathKey}:basicSettingContainer.totalWordCount2`)}
                </a>
                ？）
              </div>
            </div>
          </div>
        </>
      )}
      {!isError && !!draftDatasets.length && (
        <Button
          style={{ marginTop: 44 }}
          topic="primaryGreen"
          onClick={onEmbeddings}
        >
          {t(`${pathKey}:basicSettingContainer.import`)}
        </Button>
      )}
    </div>
  );
};

export default FileImport;
